// src/UserContext.tsx
import React, { createContext, useState, useEffect, ReactNode } from 'react';
import { ProviderProfile } from './apiClient';

interface HomeAddress {
  address_1: string;
  address_2?: string;
  city: string;
  country_id: string; // 255 for United States
  state: string; // the state abbreviation, e.g. CA or TN
  state_other?: string;
  zip_code: string;
}

// This billing info is specific to the needs of the Zenoti api
// https://docs.zenoti.com/reference/add-a-creditdebit-card-for-a-guest-service-booking-and-billing
interface BillingInfo {
  first_name: string;
  last_name: string;
  name: string; // Full name of the guest.
  email: string;
  phone: string;
  country: string;
  address: string;
  address2?: string;
  house_number: string;
  city: string;
  state: string;
  zipcode: string;
}

export interface UserData {
  zipcode: string;
  service_ids: string[]; // For services that require multiple service_ids
  date: string; // the day, e.g. YYYY-MM-DD
  timeSlot?: string; // the datetime, e.g. YYYY-MM-DDTHH:MM:SS
  provider_id?: string;
  providerProfile?: ProviderProfile;
  email: string;
  password: string;
  customer_id: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  gender: string;
  birthDate: string; // the day, e.g. YYYY-MM-DD
  city?: string;
  state?: string;
  paymentFormUrl?: string;
  appointmentConfirmed: boolean;
  homeAddress?: HomeAddress;
  billingInfo?: BillingInfo;
}

interface UserContextProps {
  userData: UserData;
  setUserData: React.Dispatch<React.SetStateAction<UserData>>;
}

export const UserContext = createContext<UserContextProps | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [userData, setUserData] = useState<UserData>(() => {
    // Rehydrate state from localStorage if it exists, otherwise use the default initial state
    const savedUserData = localStorage.getItem('userData');
    return savedUserData ? JSON.parse(savedUserData) : {};
  });

  useEffect(() => {
    // Save the userData to localStorage whenever it changes
    localStorage.setItem('userData', JSON.stringify(userData));
  }, [userData]);

  // Expose setUserData and userData to the window object for debugging
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      (window as any).setUserData = (newData: Partial<UserData>) => {
        setUserData((prevData) => ({ ...prevData, ...newData }));
      };
      (window as any).getUserData = () => userData;
    }
  }, [userData]);

  return <UserContext.Provider value={{ userData, setUserData }}>{children}</UserContext.Provider>;
};
